<template>
  <Form
    ref="validator"
    as="div"
    :initial-values="form"
  >
    <Field
      v-for="config in configKeys"
      :key="config"
      v-slot="{ field, errors }"
      :rules="fields[config].rules"
      :name="config"
    >
      <div class="relative mt-xl first-of-type:mt-0">
        <KikLabel
          v-if="fields[config].type === 'option'"
          :label-for="config"
          :label="fields[config].label"
          :required="isRequired(config)"
        >
          <SfSelect
            v-bind="field"
            :id="config"
            class="h-[40px] bg-white text-base"
            :placeholder="$t(fields[config].label)"
            :data-testid="`form-select__${config}`"
            :invalid="errors.length > 0"
            :disabled="isDisabled(config)"
            :required="isRequired(config)"
            :model-value="field.value"
            @change="(event: any) => onChange(config, event.target.value)"
          >
            <option
              v-for="value in fields[config].value"
              :key="value.key"
              :value="value.key"
            >
              {{ value.label ? $t(value.label) : value.label }}
            </option>
          </SfSelect>
        </KikLabel>
        <KikLabel
          v-else-if="fields[config].type === 'textArea'"
          :label-for="config"
          :label="fields[config].label"
          :required="isRequired(config)"
        >
          <SfTextarea
            v-bind="field"
            :id="config"
            v-model="field.value"
            class="w-full"
            :data-testid="`form-textarea__${config}`"
            :invalid="errors.length > 0"
            :cols="20"
            :rows="20"
            :maxlength="100000"
            :required="isRequired(config)"
            @input="(event: any) => onChange(config, event.target.value)"
          />
        </KikLabel>
        <template v-else-if="fields[config].type === 'radio'">
          <div
            class="flex flex-row gap-x-xl"
          >
            <KikLabel
              v-for="method in fields[config].value"
              :key="method.key"
              :label="method.label"
              :label-for="method.key"
              :required="isRequired(config)"
              :disabled="method.disabled"
              :tabindex="method.disabled ? -1 : 0"
              class="!mt-0 flex flex-row-reverse items-center gap-2"
              @keydown.enter="method.disabled ?? onChange(config, method.key)"
              @keydown.space.prevent="method.disabled ?? onChange(config, method.key)"
            >
              <SfRadio
                v-bind="field"
                :id="method.key"
                :checked="form[config] === method.key"
                :data-testid="`form-radio__${method.key}`"
                :disabled="method.disabled"
                class="disabled:border-disabled-200"
                :name="config"
                tabindex="-1"
                @input="onChange(config, method.key)"
              >
                {{ $t(method.label) }}
              </SfRadio>
            </KikLabel>
          </div>
        </template>
        <div
          v-else-if="fields[config].type === 'password'"
          class="relative"
        >
          <KikLabel
            :label-for="config"
            :label="fields[config].label"
            :required="isRequired(config)"
          >
            <SfInput
              v-bind="field"
              :id="config"
              :placeholder="$t(fields[config].label)"
              :data-testid="`form-input__${config}`"
              :type="passwordVisible ? 'text' : 'password'"
              :invalid="errors.length > 0"
              :disabled="isDisabled(config)"
              :required="isRequired(config)"
              @input="(event: any) => onChange(config, event.target.value)"
            />
          </KikLabel>
          <div class="absolute bottom-2 right-2 cursor-pointer">
            <Component
              :is="passwordVisible ? SfIconVisibility : SfIconVisibilityOff"
              class="text-neutral-800"
              tabindex="0"
              @click="passwordVisible = !passwordVisible"
              @keydown.enter="passwordVisible = !passwordVisible"
            />
          </div>
        </div>

        <template v-else-if="fields[config].type === 'date'">
          <KikLabel
            :label-for="config"
            :label="fields[config].label"
            :required="isRequired(config)"
          >
            <div>
              <VueDatePicker
                ref="datePicker"
                v-model="form[config]"
                v-mask="`${formGetters.dateFormatForPlaceholder(locale, localizedDate, true).toUpperCase()}`"
                :text-input="{
                  enterSubmit: false,
                  tabSubmit: false,
                }"
                model-type="yyyy-MM-dd"
                :format="formGetters.dateFormatForPlaceholder(locale)"
                :name="config"
                :disabled="isDisabled(config)"
                :placeholder="formGetters.dateFormatForPlaceholder(locale, localizedDate)"
                :enable-time-picker="false"
                :disabled-dates="(date: Date) => formGetters.dateIsInFuture(date)"
                :clearable="false"
                :locale="$domain.locales[0]"
                :required="isRequired(config)"
                :cancel-text="$t('Cancel')"
                :select-text="$t('Select')"
                :aria-labels="{
                  menu: $t('Calender'),
                }"
                :ui="{
                  input: '!border-none !outline-none min-w-[80px] w-full text-base outline-none appearance-none !text-neutral-900 disabled:cursor-not-allowed disabled:bg-transparent read-only:bg-transparent',
                }"
                arrow-navigation
                :data-test-id="`form-datepicker__${config}`"
                @update:model-value="(date: Date, e: Event) => {
                  onChange(config, date)
                }"
              />
              <KikFormError
                class="relative mt-1 text-left"
                :error="form[config] === null && isRequired(config) ? $t('This field is required.') : ''"
              />
            </div>
          </KikLabel>
        </template>
        <KikLabel
          v-else
          :label-for="config"
          :label="fields[config].label"
          :required="isRequired(config)"
        >
          <SfInput
            v-if="!addressFormFields.includes(config)"
            v-bind="field"
            :id="config"
            v-model="field.value"
            :placeholder="$t(fields[config].label)"
            :data-testid="`form-input__${config}`"
            :invalid="errors.length > 0"
            :type="fields[config].type"
            :disabled="isDisabled(config)"
            :required="isRequired(config)"
            @input="(event: any) => onChange(config, event.target.value)"
          />
          <KikAddressAutocomplete
            v-else
            v-bind="field"
            v-model="field.value"
            :fields="fields"
            :config="config"
            :errors="errors"
            :disabled="disabled"
            :address="addressAsObject"
            @update:model-value="(value: string) => onChange(config, value)"
            @update:address="(value: object) => updateAddress(value)"
          />
        </KikLabel>
        <KikFormError
          class="relative mt-1 text-left"
          :error="errors[0]"
        />
      </div>
    </Field>
    <div class="mt-xl">
      <slot />
    </div>
  </Form>
</template>

<script lang="ts" setup>
import {
  SfSelect,
  SfTextarea,
  SfRadio,
  SfInput,
  SfIconVisibility,
  SfIconVisibilityOff,
  useFocusVisible,
} from '@storefront-ui/vue';
import type { Form } from '~/components/Kik/DynamicForm/checkout-form';
import { useAppLocale } from '~/composables/custom/useAppLocale';
import { formGetters } from '~/composables/getters';

interface Props {
  fields: Form;
  byKeyOrder?: string[];
  values?: Record<string, any>;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  byKeyOrder: () => [],
  values: () => ({}),
  disabled: false,
});

const { isFocusVisible } = useFocusVisible({ isTextInput: false });

const emit = defineEmits(['on-form-update']);

const passwordVisible = ref(false);

const addressFormFields = ['streetName', 'streetNumber', 'additionalStreetInfo', 'postalCode', 'city', 'country'];
const addressAsObject = computed(() => {
  return {
    streetName: props.values.streetName ?? '',
    streetNumber: props.values.streetNumber ?? '',
    postalCode: props.values.postalCode ?? '',
    city: props.values.city ?? '',
  };
});

const datePickerClasses = computed(() => unref(isFocusVisible) ? '2px solid' : 'none');

const initialForm = computed<Partial<Record<string, any>>>(() =>
  props.byKeyOrder?.reduce((acc, curr) => {
    const currentValueOrDefault = props.values?.[curr] || props.fields?.[curr]?.default;
    if (currentValueOrDefault) {
      return {
        ...acc,
        [curr]: currentValueOrDefault,
      };
    }
    return acc;
  }, {}),
);

const form = ref(initialForm.value);
const validator = ref();
const {
  $flags: { flags = [] },
  $i18n,
  $domain,
} = useNuxtApp();
const { locale } = useAppLocale();

const localizedDate = {
  day: $i18n.t('day'),
  month: $i18n.t('month'),
  year: $i18n.t('year'),
};

watch(
  form,
  async (newValue) => {
    emit('on-form-update', newValue);
  },
  { immediate: true },
);

const existsInFieldsAndNotDisabledByFF = (key: string) =>
  Object.hasOwn(props.fields, key) && (!props.fields[key].featureFlag || flags.includes(props.fields[key].featureFlag as Flag));

const configKeys = computed(() => {
  const listOfKeys = props.byKeyOrder.length ? props.byKeyOrder : Object.keys(props.fields);
  return listOfKeys?.filter(existsInFieldsAndNotDisabledByFF).filter(Boolean);
});

const cleanErrorsAndReplace = (newForm: unknown, resetErrors: boolean) => {
  if (resetErrors) {
    validator.value?.reset();
  }
  form.value = newForm as Record<string, any>;
};

const onChange = async (config: string, value: string | Date) => {
  if (config !== 'dateOfBirth' && form.value?.[config] === value) {
    return;
  }

  const onChangeAction = props.fields[config].onChange;

  if (!onChangeAction) {
    form.value = { ...form.value, [config]: value };

    return;
  }

  if (typeof onChangeAction === 'object') {
    if (onChangeAction.clear) {
      const clearFields = onChangeAction.clear?.reduce(
        (acc, curr) => ({
          ...acc,
          [curr]: undefined,
        }),
        {},
      );
      const newForm = { ...form.value, [config]: value, ...clearFields };
      cleanErrorsAndReplace(newForm, false);
    }

    return;
  }

  switch (onChangeAction) {
    case 'trim':
      form.value = { ...form.value, [config]: typeof value === 'string' ? value.trim() : value };
      break;
    case 'number':
      form.value = {
        ...form.value,
        [config]: typeof value === 'string' ? value.replace(/^0+|\D/g, '') : value,
      };
      break;
    case 'clear':
      cleanErrorsAndReplace({ [config]: value }, true);
      break;
    case 'toUpperCase':
      form.value = {
        ...form.value,
        [config]: typeof value === 'string' ? value.toUpperCase() : value,
      };
      break;
    default:
  }
};

const isDisabled = (config: string) => props.disabled || !!props.fields[config]?.disabled;

const isRequired = (config: string) => {
  const rules = props.fields?.[config]?.rules;
  if (!rules) {
    return false;
  }
  if (typeof rules === 'object') {
    return rules.required as boolean;
  }
  return rules.split('|').includes('required');
};

const updateAddress = (address: any) => {
  Object.keys(address).forEach((key) => {
    if (address[key] !== undefined) {
      onChange(key, address[key]);
    }
  });
};

defineExpose({
  isInvalid: () => {
    if (!validator.value.meta.dirty) {
      return false;
    }

    const validatorErrors = Object.keys(validator.value?.errors) || [];
    if (validatorErrors.length > 0) {
      const el = document.getElementById(validatorErrors[0]);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return validatorErrors.length > 0;
  },
  clearForm: () => {
    // Reset validator state and then clear form values
    if (typeof validator.value?.resetForm === 'function') {
      validator.value.resetForm({ values: {} });
    }
    else if (typeof validator.value?.reset === 'function') {
      validator.value.reset();
    }
    form.value = {};
  },
  errors: () => validator.value?.errors,
});
</script>

<style>
.dp__input_wrap {
  @apply !w-full flex items-center gap-2 bg-white !rounded-md !border-none h-[40px] !transition-none ring-1 text-neutral-700 hover:ring-primary-700 active:ring-primary-700 active:ring-2  ring-neutral-300;
}

.dp__input_wrap:focus-within {
  outline: v-bind(datePickerClasses);
  outline-offset: 2px;
  outline-color: #6781a9;
}
</style>
